import { useCallback, useEffect, useState } from "react";
import { api } from "services/api.service";
import { AssetRetrievalDevice, DeviceType, BoxType } from "types/assetRetrieval";

export type AssetRetrieval = {
  orderNumber: string;
  employeeFirstName: string;
  employeeLastName: string;
  employeeEmail: string;
  employeePhone: string;
  employeeAddress: string;
  employeeAddressTwo: string;
  employeeAddressCity: string;
  employeeAddressState: string;
  employeeAddressZipCode: string;
  employeeAddressCountry: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  insuranceOption: string;
  locationId: string;
  accountingPocEmail: string;
  invoiced?: boolean;
  virtualPickup?: boolean;
  paymentType: "earnings" | "payment";
  isCustomAddress?: boolean;
  returnRecipientName: string;
  returnRecipientFirstName: string;
  returnRecipientLastName: string;
  returnRecipientEmail: string;
  returnRecipientCompany: string;
  returnRecipientAddress: string;
  returnRecipientAddressTwo: string;
  returnRecipientAddressCity: string;
  returnRecipientAddressState: string;
  returnRecipientAddressZipCode: string;
  returnRecipientAddressCountry: string;
  deviceType?: DeviceType | null;
  boxType: string | null;
  devices?: Pick<AssetRetrievalDevice, "deviceType" | "manufacturer" | "model" | "serialNumber">[];
};

export type AssetRetrievalPricing = {
  basePriceInCents: number;
  serviceFeeInCents: number;
  miscellaneousFeesInCents: number;
  insuranceTotalInCents: number;
  discountPercentage: number;
  discountInCents: number;
  taxPercentage: number;
  taxInCents: number;
  subtotalInCents: number;
  totalInCents: number;
  basePricePerItemInCents: number;
  serviceFeePerItemInCents: number;
  miscellaneousFeesPerItemInCents: number;
  waivedFees: boolean;
};

export const defaultDevice = {
  deviceType: DeviceType.laptop,
  manufacturer: "",
  model: "",
  serialNumber: "",
  _tempId: `temp-${Date.now()}-${Math.random()}`,
};

const dummyAssetRetrieval: AssetRetrieval = {
  orderNumber: "",
  employeeFirstName: "",
  employeeLastName: "",
  employeeEmail: "",
  employeePhone: "",
  employeeAddress: "",
  employeeAddressTwo: "",
  employeeAddressCity: "",
  employeeAddressState: "",
  employeeAddressZipCode: "",
  employeeAddressCountry: "United States",
  manufacturer: "",
  model: "",
  serialNumber: "",
  insuranceOption: "0",
  locationId: "",
  accountingPocEmail: "",
  virtualPickup: false,
  paymentType: "earnings",
  isCustomAddress: false,
  returnRecipientName: "",
  returnRecipientFirstName: "",
  returnRecipientLastName: "",
  returnRecipientEmail: "",
  returnRecipientCompany: "",
  returnRecipientAddress: "",
  returnRecipientAddressTwo: "",
  returnRecipientAddressCity: "",
  returnRecipientAddressState: "",
  returnRecipientAddressZipCode: "",
  returnRecipientAddressCountry: "United States",
  deviceType: null,
  boxType: null,
  devices: [defaultDevice],
};

const boxTypeCalculation = (boxType: string | null, deviceTypes: DeviceType[]) => {
  if (boxType !== BoxType.laptop) {
    return boxType;
  }

  if (deviceTypes.length === 1) {
    return BoxType.laptop;
  }

  if (deviceTypes.includes(DeviceType.laptop) && deviceTypes.includes(DeviceType.tablet)) {
    return BoxType.laptopTablet;
  }

  if (deviceTypes.includes(DeviceType.laptop) && deviceTypes.includes(DeviceType.mobile_phone)) {
    return BoxType.laptopMobile;
  }
};

export default function useAssetRetrieval(pricingParams?: {
  quantity?: number;
  insuranceTotalInCents?: number;
  virtualPickup?: boolean;
  boxType?: string;
  country?: string;
}) {
  const [assetRetrieval, setAssetRetrieval] = useState(dummyAssetRetrieval);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState<AssetRetrievalPricing>();

  useEffect(() => {
    setLoading(true);
    api
      .get("/asset_retrievals/pricing", {
        params: {
          quantity: pricingParams?.quantity ?? 1,
          insurance_total_in_cents: pricingParams?.insuranceTotalInCents ?? 0,
          virtual_pickup: pricingParams?.virtualPickup ?? false,
          box_type: pricingParams?.boxType ?? null,
          country: pricingParams?.country ?? null,
        },
      })
      .then((response) => {
        setPricing(response.data.pricing);
        setLoading(false);
      });
  }, [
    pricingParams?.boxType,
    pricingParams?.quantity,
    pricingParams?.insuranceTotalInCents,
    pricingParams?.virtualPickup,
    pricingParams?.country,
  ]);

  const create = useCallback(
    ({
      employeeFirstName,
      employeeLastName,
      employeeEmail,
      employeePhone,
      employeeAddress,
      employeeAddressTwo,
      employeeAddressCity,
      employeeAddressState,
      employeeAddressZipCode,
      employeeAddressCountry,
      manufacturer,
      model,
      serialNumber,
      locationId,
      insuranceOption,
      invoiced,
      paymentType,
      virtualPickup,
      returnRecipientFirstName,
      returnRecipientLastName,
      returnRecipientEmail,
      returnRecipientCompany,
      returnRecipientAddress,
      returnRecipientAddressTwo,
      returnRecipientAddressCity,
      returnRecipientAddressState,
      returnRecipientAddressZipCode,
      returnRecipientAddressCountry,
      boxType,
      devices,
    }: AssetRetrieval) => {
      return api.post("/asset_retrievals", {
        asset_retrieval: {
          employeeFirstName,
          employeeLastName,
          employeeEmail,
          employeePhone,
          employeeAddress,
          employeeAddressTwo,
          employeeAddressCity,
          employeeAddressState,
          employeeAddressZipCode,
          employeeAddressCountry,
          manufacturer,
          model,
          serialNumber,
          locationId,
          insuranceOption,
          invoiced,
          paymentType,
          virtualPickup,
          returnRecipientFirstName,
          returnRecipientLastName,
          returnRecipientEmail,
          returnRecipientCompany,
          returnRecipientAddress,
          returnRecipientAddressTwo,
          returnRecipientAddressCity,
          returnRecipientAddressState,
          returnRecipientAddressZipCode,
          returnRecipientAddressCountry,
          boxType: boxTypeCalculation(boxType, devices?.map((device) => device.deviceType) ?? []),
          devices,
        },
      });
    },
    []
  );

  const cancel = useCallback((uuid: string, cancellationReason?: string) => {
    return api.put(`/asset_retrievals/${uuid}/cancel`, {
      cancellation_reason: cancellationReason,
    });
  }, []);

  return { assetRetrieval, loading, create, cancel, pricing };
}
